import type { z } from 'zod';
import type { FiatCurrency } from '../types/FiatCurrency.types';
import type { PaginationParams } from '../types/Pagination.types';
import type { CreateDepositLimitPayload, CreateOrderDto, DepositLimitDto, UpdateDepositLimitPayload } from './orders.schema';
import { validateWithZod } from '@/utils/zod.utils';
import { ENDPOINTS } from '../endpoints';
import { httpClient } from '../httpClient';
import { PaginationParamsSchema, PaginationSchema } from '../types/Pagination.types';
import { CreateOrderSchema, DepositLimitDtoSchema, DepositLimitSchema, OrderDtoSchema, UpdateDepositLimitSchema } from './orders.schema';

export const ordersApi = {
  async create(params: CreateOrderDto, headers?: Record<string, string>) {
    const validatedParams = validateWithZod(CreateOrderSchema, params);

    const response = await httpClient.post<z.infer<typeof OrderDtoSchema>>(
      ENDPOINTS.orders.create,
      validatedParams,
      {
        headers,
      },
    );

    return validateWithZod(OrderDtoSchema, response);
  },

  async getAll(params?: PaginationParams<any>) {
    const validatedParams = validateWithZod(PaginationParamsSchema(), params);

    const response = await httpClient.get(
      ENDPOINTS.orders.all,
      { params: validatedParams },
    );

    return validateWithZod(PaginationSchema(OrderDtoSchema), response);
  },

  async getById(id: string) {
    const response = await httpClient.get<z.infer<typeof OrderDtoSchema>>(
      ENDPOINTS.orders.getById(id),
    );

    return validateWithZod(OrderDtoSchema, response);
  },

  async createDepositLimit(
    body: CreateDepositLimitPayload,
  ) {
    const validatedBody = validateWithZod(DepositLimitSchema, body);
    const response = await httpClient.post<DepositLimitDto>(
      ENDPOINTS.user.depositLimit(),
      validatedBody,
    );

    return validateWithZod(DepositLimitDtoSchema, response);
  },

  async getActiveDepositLimit(currency: FiatCurrency) {
    const response = await httpClient.get<DepositLimitDto>(
      ENDPOINTS.user.getActiveDepositLimit(),
      { params: { filters: { currency } } },
    );
    return validateWithZod(DepositLimitDtoSchema, response);
  },

  async updateDepositLimit(depositLimitId: string, body: UpdateDepositLimitPayload) {
    const validatedBody = validateWithZod(UpdateDepositLimitSchema, body);
    const response = await httpClient.patch<DepositLimitDto>(
      ENDPOINTS.user.updateDepositLimit(depositLimitId),
      validatedBody,
    );
    return validateWithZod(DepositLimitDtoSchema, response);
  },
};
